import { faFilter, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import { AccordionCollapse, Dropdown, FormGroup } from "react-bootstrap";
import Accordion from "react-bootstrap/Accordion";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import DropdownItem from "react-bootstrap/DropdownItem";
import DropdownMenu from "react-bootstrap/DropdownMenu";
import DropdownToggle from "react-bootstrap/DropdownToggle";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { FormattedMessage, useIntl } from "react-intl";
import { USER } from "../../../../common/constant";
import ObjectUtils, { coalesce } from "../../../../utils/ObjectUtils";
import { processCheckboxLabel } from "../../../../utils/OptionsUtils";
import SurveyUtils from "../../../../utils/SurveyUtils";
import AccordionCardHeaderToggle from "../../../common/AccordionToggleButton";
import Checkbox from "../../../common/Checkbox";
import Visibility from "../../../common/VisibleContainer";
import { LegendTable } from "../Charts/Charts";
import { formatNumber } from "./NumberFormatUtils";

const FacetSidebar = ({
  timeFacet,
  fundingAreaList,
  selectedFundingArea,
  selectedProgram,
  selectedProgramInstanceIds,
  target,
  status,
  projectCount,
  surveysByReportingYear,
  tooltip,
  label,
  handleSelectTimeFacet,
  handleSelectProgramInstanceFacet,
  handleSelectStatus,
  handleClickDownload,
  handleClickDownloadTexts,
  handleClickDownloadZip,
  handleClickDownloadPdf,
  handleApplyFilterCriteria
}) => {
  const intl = useIntl();

  let fundingAreas = ObjectUtils.getDeepProperty(fundingAreaList, target);
  let fundingAreaIdx = coalesce(
    ObjectUtils.findObjectIndexInArray(
      coalesce(fundingAreas, []),
      "id",
      coalesce(ObjectUtils.getDeepProperty(selectedFundingArea, target, "id"), "-1")
    ),
    -1
  );
  let currFundingArea = ObjectUtils.getDeepProperty(fundingAreas, fundingAreaIdx);

  let programs = ObjectUtils.getDeepProperty(currFundingArea, "programs");
  let programIdx = coalesce(
    ObjectUtils.findObjectIndexInArray(
      coalesce(programs, []),
      "id",
      coalesce(ObjectUtils.getDeepProperty(selectedProgram, target, "id"), "-1")
    ),
    -1
  );
  let currProgram = ObjectUtils.getDeepProperty(programs, programIdx);

  let programInstances = ObjectUtils.getDeepProperty(currProgram, "programInstances");

  const buttonTooltip = (langKey) => {
    return <Tooltip className={"programTooltip programButtonTooltip"}>{intl.formatMessage({ id: langKey })}</Tooltip>;
  };

  const surveyEntries = Object.entries(surveysByReportingYear || {});

  const renderSurveyTemplatesTooltip = () => (
    <Tooltip id="surveyTemplatesTooltip" className="programTooltip surveyTemplatesTooltip">
      <div className="text-start">
        <div className="fw-bold mb-1">{intl.formatMessage({ id: "collapsible.filter.surveyTemplates" })}</div>
        {surveyEntries.map(([year, surveys]) => (
          <div key={year} className="mb-1">
            <div>{SurveyUtils.getReportPeriodLabel(intl, currProgram?.name, year)}</div>
            <ul className="mb-0 ps-3">
              {(surveys || []).map((survey, index) => (
                <li key={`${year}-${survey.surveyRefId}-${survey.surveyVersion}-${index}`}>
                  {survey.surveyName || survey.surveyRefId}
                  {survey.surveyVersion ? ` (v${survey.surveyVersion})` : ""}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Tooltip>
  );

  return (
    <>
      <Button
        disabled={
          currFundingArea?.name !== "Aktivitätenmonitoring" &&
          (!selectedProgram ||
            !selectedProgram.indicatorsModalDialog ||
            !label ||
            label.length === 0 ||
            !selectedProgramInstanceIds ||
            selectedProgramInstanceIds.length === 0)
        }
        className="w-100"
        onClick={handleApplyFilterCriteria}
      >
        <FontAwesomeIcon icon={faFilter} className="me-2" />
        <FormattedMessage id="indicator.chart.apply.filters" />
      </Button>
      <Accordion className="my-3" defaultActiveKey="time-facet">
        <Card>
          <AccordionCardHeaderToggle label="collapsible.filter.reportingYear" eventKey="time-facet" tooltip={tooltip} />
          <AccordionCollapse eventKey="time-facet">
            <Card.Body className="pb-0">
              {timeFacet &&
                timeFacet.facets.map((checkBox, index) => (
                  <FormGroup className="mb-3" key={index}>
                    <Checkbox
                      type="checkbox"
                      name={checkBox.label}
                      value={checkBox.label}
                      defaultChecked={label && label.indexOf(checkBox.label) > -1}
                      onChange={handleSelectTimeFacet}
                    >
                      {SurveyUtils.getReportPeriodLabel(intl, selectedProgram?.indicatorsModalDialog?.name, checkBox.label)}
                    </Checkbox>
                  </FormGroup>
                ))}
            </Card.Body>
          </AccordionCollapse>
        </Card>
      </Accordion>
      {programInstances && (
        <Accordion className="mb-3" defaultActiveKey="instance-facet">
          <Card>
            <AccordionCardHeaderToggle
              label="master.data.management.program.instance"
              eventKey="instance-facet"
              onReset={(e) => handleSelectProgramInstanceFacet(e, programInstances)}
            />
            <AccordionCollapse eventKey="instance-facet">
              <Card.Body className="pb-0">
                {programInstances.map((progInst, index) => (
                  <FormGroup className="mb-3" key={index}>
                    <Checkbox
                      type="checkbox"
                      value={progInst.id}
                      onChange={handleSelectProgramInstanceFacet}
                      checked={selectedProgramInstanceIds && selectedProgramInstanceIds.indexOf(progInst.id) > -1}
                      title={progInst.name}
                    >
                      {processCheckboxLabel(progInst)}
                    </Checkbox>
                  </FormGroup>
                ))}
              </Card.Body>
            </AccordionCollapse>
          </Card>
        </Accordion>
      )}
      <FormGroup  className="mb-3">
        <Checkbox
          id={"status"}
          name={"status"}
          onChange={handleSelectStatus}
          checked={status === "SENT"}
          type={"checkbox"}
        >
          {intl.formatMessage({ id: "collapsible.filter.status" })}
        </Checkbox>
      </FormGroup>

      {/*<div className="status__facet">
             <Checkbox id={"finalReport"} name={"finalReport"} type={"checkbox"}>
             {intl.formatMessage({"id": "collapsible.filter.finalReport"})}
             </Checkbox>
             </div>*/}

      <FormGroup  className="mb-3">
        <p>
          {intl.formatMessage({ id: "collapsible.filter.projectCount" })}
          <b>{formatNumber(projectCount || 0)}</b>
          {surveyEntries.length > 0 && (
            <OverlayTrigger placement="left" overlay={renderSurveyTemplatesTooltip()}>
              <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-info" role="button" />
            </OverlayTrigger>
          )}
        </p>
      </FormGroup>

      <Dropdown>
        <DropdownToggle className="w-100" disabled={!projectCount}>
          <FormattedMessage id="app.common.Export" />
        </DropdownToggle>
        <DropdownMenu>
          <OverlayTrigger placement="top" overlay={buttonTooltip("collapsible.download.downloadZip.helperText")}>
            <DropdownItem
              disabled={
                !(
                  selectedFundingArea &&
                  selectedFundingArea.indicatorsModalDialog &&
                  selectedFundingArea.indicatorsModalDialog.foreignId === "AMON"
                ) &&
                (!selectedProgram || !selectedProgram.indicatorsModalDialog)
              }
              onClick={handleClickDownloadZip}
            >
              <FormattedMessage id="collapsible.download.downloadZip" />
            </DropdownItem>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={buttonTooltip("collapsible.download.downloadAll.helperText")}>
            <DropdownItem
              disabled={
                !(
                  selectedFundingArea &&
                  selectedFundingArea.indicatorsModalDialog &&
                  selectedFundingArea.indicatorsModalDialog.foreignId === "AMON"
                ) &&
                (!selectedProgram || !selectedProgram.indicatorsModalDialog)
              }
              onClick={handleClickDownload}
            >
              <FormattedMessage id="collapsible.download.downloadAll" />
            </DropdownItem>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={buttonTooltip("collapsible.download.downloadTexts.helperText")}>
            <DropdownItem
              disabled={
                !(
                  selectedFundingArea &&
                  selectedFundingArea.indicatorsModalDialog &&
                  selectedFundingArea.indicatorsModalDialog.foreignId === "AMON"
                ) &&
                (!selectedProgram || !selectedProgram.indicatorsModalDialog)
              }
              onClick={handleClickDownloadTexts}
            >
              <FormattedMessage id="collapsible.download.downloadTexts" />
            </DropdownItem>
          </OverlayTrigger>
          <Visibility roles={[USER]}>
            <OverlayTrigger placement="top" overlay={buttonTooltip("collapsible.download.downloadPdf.helperText")}>
              <DropdownItem
                disabled={
                  !(
                    selectedFundingArea &&
                    selectedFundingArea.indicatorsModalDialog &&
                    selectedFundingArea.indicatorsModalDialog.foreignId === "AMON"
                  ) &&
                  (!selectedProgram || !selectedProgram.indicatorsModalDialog)
                }
                onClick={handleClickDownloadPdf}
              >
                <FormattedMessage id="collapsible.download.downloadPdf" />
              </DropdownItem>
            </OverlayTrigger>

            {SurveyUtils.isSemesterTypeProgram(currProgram?.name) && (
              <>
                <OverlayTrigger
                  placement="top"
                  overlay={buttonTooltip("collapsible.download.downloadAAPdf.helperText")}
                >
                  <DropdownItem
                    disabled={
                      !(
                        selectedFundingArea &&
                        selectedFundingArea.indicatorsModalDialog &&
                        selectedFundingArea.indicatorsModalDialog.foreignId === "AMON"
                      ) &&
                      (!selectedProgram || !selectedProgram.indicatorsModalDialog)
                    }
                    onClick={() => handleClickDownloadPdf("AA")}
                  >
                    <FormattedMessage id="collapsible.download.downloadAAPdf" />
                  </DropdownItem>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={buttonTooltip("collapsible.download.downloadRueckPdf.helperText")}
                >
                  <DropdownItem
                    disabled={
                      !(
                        selectedFundingArea &&
                        selectedFundingArea.indicatorsModalDialog &&
                        selectedFundingArea.indicatorsModalDialog.foreignId === "AMON"
                      ) &&
                      (!selectedProgram || !selectedProgram.indicatorsModalDialog)
                    }
                    onClick={() => handleClickDownloadPdf("RUECK")}
                  >
                    <FormattedMessage id="collapsible.download.downloadRueckPdf" />
                  </DropdownItem>
                </OverlayTrigger>
              </>
            )}
          </Visibility>
        </DropdownMenu>
      </Dropdown>
      <LegendTable />
    </>
  );
};

FacetSidebar.propTypes = {
  timeFacet: PropTypes.object,
  selectedFundingArea: PropTypes.object,
  selectedProgram: PropTypes.object,
  status: PropTypes.string,
  projectCount: PropTypes.number,
  surveysByReportingYear: PropTypes.object,
  tooltip: PropTypes.object,
  label: PropTypes.array,
  handleSelectTimeFacet: PropTypes.func,
  handleSelectStatus: PropTypes.func,
  handleClickDownload: PropTypes.func,
  handleClickDownloadTexts: PropTypes.func,
  handleClickDownloadZip: PropTypes.func,
  handleClickDownloadPdf: PropTypes.func
};

export default FacetSidebar;
